import { useQuery } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState } from "react";
import { accountingApi, formatInr, type FinancialYear } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

export default function AccountingTrialBalancePage() {
  const { hospitalId } = useAccountingHospitalContext();
  const [financialYearId, setFinancialYearId] = useState<string>("");
  const [periodId, setPeriodId] = useState<string>("");

  const { data: years = [] } = useQuery({
    queryKey: ["accounting-years", hospitalId],
    queryFn: () => accountingApi.listFinancialYears(hospitalId),
    enabled: !!hospitalId,
  });

  const selectedYear = (years as FinancialYear[]).find((y) => y.id === financialYearId);

  const { data: tb, isLoading, isFetching } = useQuery({
    queryKey: ["accounting-tb", hospitalId, financialYearId, periodId],
    queryFn: () =>
      accountingApi.trialBalance(hospitalId, {
        financialYearId: financialYearId || undefined,
        accountingPeriodId: periodId || undefined,
      }),
    enabled: !!hospitalId,
  });

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-medium">Trial balance</h2>
      <div className="flex flex-wrap gap-3">
        <div className="w-56 space-y-1">
          <Label className="text-xs">Financial year</Label>
          <Select
            value={financialYearId || "all"}
            onValueChange={(v) => {
              setFinancialYearId(v === "all" ? "" : v);
              setPeriodId("");
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All posted" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All posted</SelectItem>
              {(years as FinancialYear[]).map((y) => (
                <SelectItem key={y.id} value={y.id}>
                  {y.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-56 space-y-1">
          <Label className="text-xs">Period</Label>
          <Select
            value={periodId || "all"}
            onValueChange={(v) => setPeriodId(v === "all" ? "" : v)}
            disabled={!selectedYear}
          >
            <SelectTrigger>
              <SelectValue placeholder="Entire FY / all" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Entire FY / all</SelectItem>
              {(selectedYear?.periods || []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Debit</TableHead>
              <TableHead className="text-right">Credit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading || isFetching ? (
              <TableRow>
                <TableCell colSpan={5}>Loading…</TableCell>
              </TableRow>
            ) : !tb?.rows?.length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-slate-500">
                  No posted balances.
                </TableCell>
              </TableRow>
            ) : (
              <>
                {tb.rows.map((r) => (
                  <TableRow key={r.ledgerAccountId}>
                    <TableCell>{r.code}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.accountType}</TableCell>
                    <TableCell className="text-right">
                      {r.debit ? formatInr(r.debit) : ""}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.credit ? formatInr(r.credit) : ""}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} className="font-semibold">
                    Total {tb.balanced ? "(balanced)" : "(OUT OF BALANCE)"}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatInr(tb.totalDebit)}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatInr(tb.totalCredit)}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
